import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, LandPlot, MapPin, Eye } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export function MyPlots() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['my-plots', user?.id],
    queryFn: async () => {
      if (!user) return { plots: [] };
      const { data, error } = await supabase
        .from('plots')
        .select('*, plot_images(url, is_primary)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { plots: data || [] };
    },
    enabled: !!user,
  });

  const plots = (data as any)?.plots || [];

  const formatPrice = (price: number, currency: string) => {
    if (currency === 'USD') return `$${price.toLocaleString()}`;
    return `ETB ${price.toLocaleString()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-emerald-100 text-emerald-700';
      case 'draft': return 'bg-muted text-muted-foreground';
      case 'sold': return 'bg-blue-100 text-blue-700';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getApprovalColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-emerald-100 text-emerald-700';
      case 'pending_review': return 'bg-amber-100 text-amber-700';
      case 'rejected': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LandPlot className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl font-bold">My Plots</h2>
        </div>
        <Button onClick={() => navigate('/dashboard/plots/new')}>
          <Plus className="h-4 w-4 mr-1" />Add Plot
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      ) : plots.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <LandPlot className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
            <h3 className="font-semibold text-lg mb-1">No plots listed yet</h3>
            <p className="text-muted-foreground text-sm mb-4">Start by adding your first plot or land listing.</p>
            <Button onClick={() => navigate('/dashboard/plots/new')}><Plus className="h-4 w-4 mr-1" />Add Your First Plot</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {plots.map((plot: any) => {
            const primaryImg = plot.plot_images?.find((img: any) => img.is_primary)?.url || plot.plot_images?.[0]?.url;
            const title = typeof plot.title === 'object' ? (plot.title.en || plot.title.am || 'Untitled') : plot.title;
            return (
              <Card key={plot.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-36 bg-muted">
                  {primaryImg ? (
                    <img src={primaryImg} alt={title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full"><LandPlot className="h-10 w-10 text-muted-foreground" /></div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Badge className={getStatusColor(plot.status)}>{plot.status}</Badge>
                    <Badge className={getApprovalColor(plot.approval_status)}>{plot.approval_status?.replace('_', ' ')}</Badge>
                  </div>
                </div>
                <CardContent className="p-4 space-y-2">
                  <h3 className="font-semibold text-sm line-clamp-1">{title}</h3>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <MapPin className="h-3 w-3" />{plot.city}{plot.sub_city ? `, ${plot.sub_city}` : ''}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-primary">{formatPrice(plot.price, plot.currency)}</span>
                    {plot.area_sqm && <span className="text-xs text-muted-foreground">{plot.area_sqm} m²</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
